import { StringUtils } from './StringUtils';

/**
 * Utility functions to build structured expressions for filtering, math, and logic.
 * @namespace
 */
export class ExprUtils {

  // Logical Operators
  static and(...exprs) {
    return ExprUtils.join('AND', ...exprs);
  }

  static or(...exprs) {
    return ExprUtils.join('OR', ...exprs);
  }

  static join(exprType, ...exprs) {
    const expr = {
      exprType: exprType,
      exprs: exprs.filter(expr => expr != null)
    };

    return expr.exprs.length === 0 ? null : expr;
  }

  // Field Expressions
  static fieldExpr(fieldName, exprType, value) {
    let valueType;
    let caseSensitive = true;
    if (typeof value === 'number') {
      valueType = 'NUMBER';
    } else if (typeof value === 'boolean') {
      valueType = 'BOOLEAN';
    } else if (value == null) {
      valueType = 'NULL';
    } else if (Array.isArray(value)) {
      valueType = 'LIST'
    } else if (StringUtils.isISODate(value)) {
      valueType = 'TIMESTAMP';
    } else {
      valueType = 'LITERAL';
      caseSensitive = false;
    }

    const exprs = [{ exprType: 'VALUE', valueType: 'VARIABLE', value: fieldName, caseSensitive }];
    if (value != null) {
      exprs.push({ exprType: 'VALUE', valueType, value, caseSensitive });
    }

    return { exprType, exprs };
  }

  // Mathematical Expressions
  static plus(...args) {
    return { exprType: 'PLUS', exprs: args.map(arg => ExprUtils.exprFromArg(arg)) };
  }

  static minus(arg1, arg2) {
    return { exprType: 'MINUS', exprs: [ExprUtils.exprFromArg(arg1), ExprUtils.exprFromArg(arg2)] };
  }

  static abs(arg) {
    return { exprType: 'ABS', exprs: [ExprUtils.exprFromArg(arg)] };
  }

  static isGreater(arg1, arg2) {
    return { exprType: 'IS_GREATER', exprs: [ExprUtils.exprFromArg(arg1), ExprUtils.exprFromArg(arg2)] };
  }

  static isGreaterOrEqual(arg1, arg2) {
    return { exprType: 'IS_GREATER_OR_EQUAL', exprs: [ExprUtils.exprFromArg(arg1), ExprUtils.exprFromArg(arg2)] };
  }

  static isLess(arg1, arg2) {
    return { exprType: 'IS_LESS', exprs: [ExprUtils.exprFromArg(arg1), ExprUtils.exprFromArg(arg2)] };
  }

  static isLessOrEqual(arg1, arg2) {
    return { exprType: 'IS_LESS_OR_EQUAL', exprs: [ExprUtils.exprFromArg(arg1), ExprUtils.exprFromArg(arg2)] };
  }

  static effectiveDateClause(field, startDate, endDate) {
    return ExprUtils.and(
      ExprUtils.isGreaterOrEqual(field, startDate),
      ExprUtils.isLessOrEqual(field, endDate)
    );
  }

  static exprFromArg(arg) {
    if (typeof arg === 'string') {
      return { exprType: 'VALUE', valueType: 'VARIABLE', value: arg, caseSensitive: true };
    } else if (typeof arg === 'number') {
      return { exprType: 'VALUE', valueType: 'NUMBER', value: arg, caseSensitive: true };
    } else {
      return arg;
    }
  }

  // Filters
  static filterModelToExpr(filterModel, fieldDefs) {
    if (filterModel == null || Object.keys(filterModel).length === 0) return null;

    // Assign any fkEntities to the filter so that the backend can reroute the filters on fkEntity columns
    const fkEntityFieldDefs = fieldDefs.filter(fieldDef => fieldDef.entity != null && fieldDef.field.endsWith('Id'));
    fkEntityFieldDefs.forEach(fieldDef => {
      const base = fieldDef.field.replace('Id', '');
      
      const codeFilter = filterModel[base + 'Code'];
      if (codeFilter) {
        codeFilter.fkColPrefix = StringUtils.toColConvention(base);
        codeFilter.fkEntity = fieldDef.entity;
      }

      const nameFilter = filterModel[base + 'Name'];
      if (nameFilter) {
        nameFilter.fkColPrefix = StringUtils.toColConvention(base);
        nameFilter.fkEntity = fieldDef.entity;
      }
    })

    const exprs = Object.entries(filterModel).map(([fieldName, filter]) => {
      let expr;
      if (filter.conditions) {
        const expr1 = ExprUtils.basicFilterToExpr(fieldName, filter.conditions[0]);
        const expr2 = ExprUtils.basicFilterToExpr(fieldName, filter.conditions[1]);
        expr = (filter.operator === 'OR' ? ExprUtils.or : ExprUtils.and)(expr1, expr2);
      } else {
        expr = ExprUtils.basicFilterToExpr(fieldName, filter);
      }

      expr.fkColPrefix = filter.fkColPrefix;
      expr.fkEntity = filter.fkEntity;
      return expr;
    });

    return exprs.length === 1 ? exprs[0] : ExprUtils.and(...exprs);
  }

  static basicFilterToExpr(fieldName, filter) {
    if (filter.filterType === 'set') {
      return ExprUtils.setExpr(fieldName, filter.values);
    }
    if (filter.type === 'inRange') {
      return ExprUtils.and(
        ExprUtils.fieldExpr(fieldName, 'IS_GREATER_OR_EQUAL', filter.filter),
        ExprUtils.fieldExpr(fieldName, 'IS_LESS_OR_EQUAL', filter.filterTo)
      );
    }

    const filterTypeToExprType = {
      equals: 'IS',
      notEqual: 'IS_NOT',
      blank: 'NOT_EXISTS',
      notBlank: 'EXISTS',
      greaterThan: 'IS_GREATER',
      greaterThanOrEqual: 'IS_GREATER_OR_EQUAL',
      lessThan: 'IS_LESS',
      lessThanOrEqual: 'IS_LESS_OR_EQUAL',
    };

    return ExprUtils.fieldExpr(fieldName, filterTypeToExprType[filter.type] ?? StringUtils.toCodeConvention(filter.type), filter.filter);
  }

  // Set Expressions
  static setExpr(fieldName, set) {
    return ExprUtils.fieldExpr(fieldName, 'IS_ONE_OF', set);
  }

  static notSetExpr(fieldName, set) {
    return ExprUtils.fieldExpr(fieldName, 'IS_NOT_ONE_OF', set);
  }

  // Conditionals
  static isNot(fieldName, value) {
    return ExprUtils.fieldExpr(fieldName, 'IS_NOT', value);
  }

  static isNotOneOf(fieldName, value) {
    return ExprUtils.fieldExpr(fieldName, 'IS_NOT_ONE_OF', value);
  }

  static is(fieldName, value) {
    return ExprUtils.fieldExpr(fieldName, 'IS', value);
  }

  // Existence
  static exists(fieldName) {
    return ExprUtils.fieldExpr(fieldName, 'EXISTS');
  }

  static notExists(fieldName) {
    return ExprUtils.fieldExpr(fieldName, 'NOT_EXISTS');
  }

  // Contains
  static contains(fieldName, value) {
    return ExprUtils.fieldExpr(fieldName, 'CONTAINS', value);
  }

  static startsWith(fieldName, value) {
    return ExprUtils.fieldExpr(fieldName, 'STARTS_WITH', value);
  }

  // Where Conditions
  static whereParams(...args) {
    const exprs = [];
    let field = null;

    args.forEach((arg, index) => {
      if (index % 2 === 0) {
        field = arg;
      } else {
        let expr;
        if (arg == null) {
          expr = ExprUtils.fieldExpr(field, 'NOT_EXISTS');
        } else if (Array.isArray(arg)) {
          expr = ExprUtils.fieldExpr(field, 'IS_ONE_OF', arg);
        } else {
          expr = ExprUtils.fieldExpr(field, 'IS', arg);
        }
        exprs.push(expr);
      }
    });

    return { exprType: 'AND', exprs };
  }
}

/**
 * @typedef {Object} Expr
 * @property {string} exprType - The type of expression (e.g., 'AND', 'IS', 'PLUS', etc.).
 * @property {Expr[] | any[]} [exprs] - Sub-expressions or values involved.
 * @property {string} [valueType] - Type of value for 'VALUE' expressions (e.g., 'VARIABLE', 'NUMBER', 'BOOLEAN').
 * @property {any} [value] - The actual value for 'VALUE' expressions.
 * @property {boolean} [caseSensitive] - Indicates if the comparison is case-sensitive.
 * @property {string} [fkCol] - Optional foreign key column.
 * @property {string} [fkEntity] - Optional foreign key entity.
 */
